// ═══════════════════════════════════════════════════════════════════
//  modules/chat/SujPanel.js
//  SujPanel
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { CATEGORY_LABELS } from "../projets/constantes";
import { CHAT_PROJECT_NAME, SUJ_CANAUX, SUJ_CATEGORIES, SUJ_CATEGORIE_IDS, SUJ_STATUTS, SUJ_TRIS } from "./constantes";
import { SujChip } from "./SujChip";
import { SujFilterRow } from "./SujFilterRow";
import { SujKpi } from "./SujKpi";
import { SujPuces } from "./SujPuces";
import { SujRow } from "./SujRow";
import { SujRunsModal } from "./SujRunsModal";
import { sujDateCourte, sujDateLongue, sujJourLong } from "./utils";

// ═══════════════ PANNEAU D'UN CANAL ════════════════════════════════════
export function SujPanel({ token, user, canal, onCanaux }) {
  const isMobile = useIsMobile();
  const canRun = !!user && (user.role === "super_admin" || user.role === "admin");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [running, setRunning] = useState(false);
  const [recapRunning, setRecapRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showRuns, setShowRuns] = useState(false);
  const [selected, setSelected] = useState([]);

  const [statut, setStatut] = useState("ouvert");
  const [categorie, setCategorie] = useState("");
  const [sort, setSort] = useState("severite");
  const [occMin, setOccMin] = useState(1);
  const [since, setSince] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [recapJour, setRecapJour] = useState("");
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const p = new URLSearchParams({ canal, statut, sort, occ_min: String(occMin), limit: String(LIMIT), offset: String(page * LIMIT) });
      if (categorie) p.set("categorie", categorie);
      if (since) p.set("since", since);
      if (q) p.set("q", q);
      if (recapJour) p.set("recap_jour", recapJour);
      const res = await fetch(`${API_BASE}/api/chat/sujets?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) { setError(json.error || "Erreur de chargement"); setData(null); }
      else { setData(json); if (json.canaux && onCanaux) onCanaux(json.canaux); }
    } catch { setError("Impossible de contacter le serveur"); }
    setLoading(false);
  }, [LIMIT, canal, categorie, occMin, onCanaux, page, q, recapJour, since, sort, statut, token]);

  useEffect(() => { setPage(0); setSelected([]); }, [canal, statut, categorie, sort, occMin, since, q]);
  useEffect(() => { load(); }, [canal, statut, categorie, sort, occMin, since, q, page, recapJour, load]);

  const setStatutSujet = async (id, nouveauStatut) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/api/chat/sujets/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ statut: nouveauStatut }),
      });
      if (!res.ok) { const j = await res.json(); setError(j.error || "Modification impossible"); }
      else await load();
    } catch { setError("Impossible de contacter le serveur"); }
    setBusyId(null);
  };

  const bulk = async (nouveauStatut) => {
    if (selected.length === 0) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/sujets`, {
        method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selected, statut: nouveauStatut }),
      });
      const j = await res.json();
      if (!res.ok) setError(j.error || "Action impossible");
      else { setNotice(`${j.modifies} sujet(s) mis à jour.`); setSelected([]); await load(); }
    } catch { setError("Impossible de contacter le serveur"); }
  };

  const forcerTache = async (sujet) => {
    setBusyId(sujet.id); setError(""); setNotice("");
    try {
      const res = await fetch(`${API_BASE}/api/chat/sujets/${sujet.id}/tache`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (!res.ok) setError(j.error + (j.alter_sql ? ` — SQL à exécuter : ${j.alter_sql}` : ""));
      else { setNotice(`Tâche #${j.task_id} ${j.etat === "creee" ? "créée" : "mise à jour"} dans le projet « ${j.project_nom} ».`); await load(); }
    } catch { setError("Impossible de contacter le serveur"); }
    setBusyId(null);
  };

  const runNow = async () => {
    setRunning(true); setNotice(""); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/chat/analysis/run`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ canal }),
      });
      const j = await res.json();
      if (!res.ok) setError(j.error || "Échec de l'analyse");
      else {
        const r = (j.resultats || {})[canal] || {};
        setNotice(r.skipped === "no_messages"
          ? `Aucun message sur ce canal pour la fenêtre du ${j.window_start} — rien à analyser.`
          : r.error ? `Erreur : ${r.error}`
          : `Analyse terminée : ${r.nouveaux || 0} nouveau(x) sujet(s), ${r.maj || 0} mis à jour, ${r.taches_creees || 0} tâche(s) créée(s), ${r.taches_majs || 0} tâche(s) mise(s) à jour.`);
        await load();
      }
    } catch { setError("Impossible de contacter le serveur"); }
    setRunning(false);
  };

  const runRecaps = async () => {
    setRecapRunning(true); setNotice(""); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/chat/recaps/run`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ canal, jour: recapJour || undefined }),
      });
      const j = await res.json();
      if (!res.ok) setError(j.error || "Échec de la génération");
      else { setNotice(`Récaps régénérés — quotidien : ${j.quotidien}, global : ${j.global}.`); await load(); }
    } catch { setError("Impossible de contacter le serveur"); }
    setRecapRunning(false);
  };

  const etatCanal = data && (data.canaux || []).find((x) => x.id === canal);
  const vide = etatCanal && !etatCanal.alimente && (!data.sujets || data.sujets.length === 0);

  if (loading && !data) return <div style={{ color: THEME.textMuted, padding: 20 }}>Chargement du registre…</div>;

  if (data && data.unavailable) return (
    <div style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", color: THEME.amber, fontSize: 13 }}>
      ⏳ La table de messages du chat n'existe pas encore — les analyses démarreront dès les premiers messages reçus du plugin.
    </div>
  );

  if (vide) return (
    <div style={{ background: THEME.panel, border: `1px dashed ${THEME.border}`, borderRadius: 14, padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 34, marginBottom: 10 }}>{(SUJ_CANAUX.find((x) => x.id === canal) || {}).icon}</div>
      <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700 }}>Ce canal n'est pas encore alimenté</div>
      {canal === "discord" ? (
        <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: 8, lineHeight: 1.55, maxWidth: 520, margin: "8px auto 0" }}>
          Noctaviel n'envoie pas encore les messages de <b>#taverne</b> et <b>#suggestion</b> vers
          <code style={{ color: THEME.gold }}> /api/chat/ingest</code>.<br />
          Il faut installer <code style={{ color: THEME.gold }}>chat-ingest.js</code> dans le bot et renseigner
          <code style={{ color: THEME.gold }}> INGEST_CHANNELS</code> — voir la notice « Global Discord ».
        </div>
      ) : (
        <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: 8, lineHeight: 1.55, maxWidth: 480, margin: "8px auto 0" }}>
          Le plugin n'envoie pas encore de messages avec <code style={{ color: THEME.gold }}>channel={canal === "mp" ? '"mp"' : '"boxchat"'}</code> sur
          <code style={{ color: THEME.gold }}> /api/chat/ingest</code>.<br />
          Rien à faire de ton côté : dès que Brice enverra ce canal, l'analyse démarre au passage suivant.
        </div>
      )}
    </div>
  );

  const cpt = (data && data.compteurs) || null;
  const sys = (data && data.systeme) || {};
  const tachesCfg = sys.taches || {};
  const runs = (data && data.derniers_passages) || [];
  const dernier = runs[0];
  const rg = data && data.recap_global;
  const rq = data && data.recap_quotidien;
  const vieServeur = (data && data.vie_serveur) || [];
  const joursRecap = (data && data.recap_jours) || [];
  const totalPages = data ? Math.ceil(data.total / LIMIT) : 1;
  const maxOcc = cpt ? Math.max(1, cpt.max_occurrences) : 1;
  const allSelected = data && data.sujets.length > 0 && selected.length === data.sujets.length;

  const carte = (bordure) => ({ background: THEME.panel, border: `1px solid ${bordure || THEME.border}`, borderRadius: 12, padding: 16 });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Alerte : la colonne category refuse les catégories de tâches */}
      {tachesCfg.actif && tachesCfg.colonne_ok === false && (
        <div style={{ background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 10, padding: "12px 16px" }}>
          <div style={{ color: THEME.red, fontSize: 13, fontWeight: 700, marginBottom: 6 }}>⚠️ Création de tâches bloquée</div>
          <div style={{ color: THEME.text, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
            La colonne <code>project_tasks.category</code> n'accepte pas encore les catégories du module.
            Exécute ce SQL dans ta base puis redémarre le backend :
          </div>
          {tachesCfg.alter_sql && (
            <div style={{ background: THEME.bg, borderRadius: 6, padding: "8px 12px", color: THEME.gold, fontSize: 11, fontFamily: "'Courier New', monospace", wordBreak: "break-all", marginBottom: 8 }}>
              {tachesCfg.alter_sql}
            </div>
          )}
          {tachesCfg.alter_sql && (
            <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(tachesCfg.alter_sql)}
              style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 11 }}>📋 Copier le SQL</button>
          )}
        </div>
      )}

      {notice && <div style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, color: THEME.amber, borderRadius: 8, padding: "9px 14px", fontSize: 12 }}>{notice}</div>}
      {error && <div style={{ background: THEME.redDim, border: `1px solid ${THEME.red}`, color: THEME.red, borderRadius: 8, padding: "9px 14px", fontSize: 12 }}>{error}</div>}

      {/* ── 1. RÉCAPS ──────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.25fr 1fr", gap: 14 }}>

        {/* Récap global */}
        <div style={{ ...carte(`${THEME.purple}55`), background: `linear-gradient(155deg, ${THEME.purpleDim}, ${THEME.panel} 62%)` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ color: THEME.purple, fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>🧭 Récap global</div>
            {rg && <span style={{ marginLeft: "auto", color: THEME.textDim, fontSize: 10 }}>
              {rg.base_jours ? `${rg.base_jours} jour(s) analysés · ` : ""}maj {sujDateCourte(rg.created_at)}
            </span>}
          </div>
          {!rg ? (
            <div style={{ color: THEME.textDim, fontSize: 12, lineHeight: 1.5 }}>
              Pas encore de synthèse de fond. Elle est produite chaque nuit à partir des récaps quotidiens
              {canRun ? " — le bouton « Régénérer les récaps » en bas de page la crée immédiatement." : "."}
            </div>
          ) : (
            <>
              <div style={{ color: THEME.text, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{rg.recap || "—"}</div>
              <SujPuces titre="Priorités" items={rg.priorites} color={THEME.amber} puce="▸" />
              <SujPuces titre="Tendances de fond" items={rg.tendances_de_fond} color={THEME.text} puce="·" />
              <SujPuces titre="Signaux positifs" items={rg.signaux_positifs} color={THEME.emerald} puce="✓" />
            </>
          )}
        </div>

        {/* Récap quotidien */}
        <div style={carte()}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <div style={{ color: THEME.gold, fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>📅 Récap quotidien</div>
            {joursRecap.length > 0 && (
              <select value={recapJour || (rq ? rq.jour : "")} onChange={(e) => setRecapJour(e.target.value)}
                style={{ marginLeft: "auto", background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 6, padding: "4px 8px", fontSize: 11, outline: "none" }}>
                {joursRecap.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            )}
          </div>
          {!rq ? (
            <div style={{ color: THEME.textDim, fontSize: 12, lineHeight: 1.5 }}>
              Aucun récap quotidien pour l'instant. Il est généré automatiquement au passage qui clôt la journée (minuit).
            </div>
          ) : (
            <>
              <div style={{ color: THEME.textMuted, fontSize: 11, textTransform: "capitalize", marginBottom: 6 }}>
                {sujJourLong(rq.jour)}{rq.nb_messages ? ` · ${rq.nb_messages} message(s)` : ""}
              </div>
              <div style={{ color: THEME.text, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{rq.recap || "—"}</div>
              <SujPuces titre="Faits marquants" items={rq.faits_marquants} color={THEME.text} puce="•" />
              {rq.vie_communaute && (
                <div style={{ marginTop: 10, background: THEME.surface, borderLeft: `3px solid #6FD3C7`, borderRadius: 6, padding: "8px 12px", color: THEME.textMuted, fontSize: 12, lineHeight: 1.5 }}>
                  🤝 {rq.vie_communaute}
                </div>
              )}
              <SujPuces titre="À surveiller" items={rq.a_surveiller} color={THEME.amber} puce="⚠" />
            </>
          )}
        </div>
      </div>

      {/* ── 2. VIE DU SERVEUR (rapport simple, sans tâche) ──────────── */}
      {vieServeur.length > 0 && (
        <div style={carte()}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ color: "#6FD3C7", fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>🤝 Vie du serveur</div>
            <span style={{ color: THEME.textDim, fontSize: 10 }}>ce que les joueurs se racontent — aucune tâche créée</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
            {vieServeur.map((v) => (
              <div key={v.id} style={{ background: THEME.surface, borderRadius: 8, padding: "9px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: THEME.text, fontSize: 12, fontWeight: 600, flex: 1 }}>{v.titre}</span>
                  <span style={{ color: "#6FD3C7", fontSize: 12, fontWeight: 700 }}>{v.occurrences}</span>
                </div>
                {v.description && <div style={{ color: THEME.textDim, fontSize: 11, marginTop: 3, lineHeight: 1.45 }}>{v.description}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 3. COMPTEURS ────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <SujKpi label={statut === "tous" ? "Sujets (tous)" : statut === "resolu" ? "Sujets résolus" : statut === "ignore" ? "Sujets ignorés" : "Sujets ouverts"}
          value={cpt ? cpt.total_sujets : "—"} color={THEME.text} active={!categorie} onClick={() => setCategorie("")} />
        {SUJ_CATEGORIE_IDS.map((id) => {
          const meta = SUJ_CATEGORIES[id];
          const v = cpt && cpt.par_categorie[id] ? cpt.par_categorie[id].n : 0;
          return <SujKpi key={id} icon={meta.icon} label={meta.label} value={v} color={meta.color}
            active={categorie === id} onClick={() => setCategorie(categorie === id ? "" : id)} />;
        })}
      </div>

      {/* État du système */}
      <div style={{ ...carte(), padding: "12px 16px", display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div style={{ color: THEME.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 5 }}>État du système</div>
          <Badge text={sys.actif ? "ACTIF" : "EN PAUSE"} bg={sys.actif ? THEME.emeraldDim : THEME.surface} color={sys.actif ? THEME.emerald : THEME.textMuted} />
        </div>
        <div>
          <div style={{ color: THEME.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 5 }}>Dernière analyse</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: THEME.text, fontSize: 12, fontWeight: 600 }}>{dernier ? sujDateLongue(dernier.window_start) : "jamais"}</span>
            {dernier && <Badge text={dernier.status === "success" ? "SUCCESS" : dernier.status === "empty" ? "VIDE" : "ERREUR"}
              bg={dernier.status === "success" ? THEME.emeraldDim : dernier.status === "empty" ? THEME.surface : THEME.redDim}
              color={dernier.status === "success" ? THEME.emerald : dernier.status === "empty" ? THEME.textMuted : THEME.red} />}
          </div>
        </div>
        <div>
          <div style={{ color: THEME.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 5 }}>Tâches automatiques</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Badge text={tachesCfg.actif ? `dès ${tachesCfg.seuil_occurrences} occ.` : "désactivées"}
              bg={tachesCfg.actif ? THEME.purpleDim : THEME.surface} color={tachesCfg.actif ? THEME.purple : THEME.textMuted} />
            <span style={{ color: THEME.textDim, fontSize: 11 }}>
              projet « {tachesCfg.projet_nom || CHAT_PROJECT_NAME} »{tachesCfg.projet_id ? ` (#${tachesCfg.projet_id})` : " — pas encore créé"}
            </span>
          </div>
        </div>
        {sys.fusion && sys.fusion.actif && (
          <div>
            <div style={{ color: THEME.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 5 }}>Fusion jeu ↔ Discord</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Badge text={`${sys.fusion.nb_sujets_partages || 0} sujet(s) partagé(s)`} bg={THEME.accentDim} color={THEME.accent} />
              <span style={{ color: THEME.textDim, fontSize: 11 }}>
                {(sys.fusion.labels || []).join(" · ")} — une seule ligne, une seule tâche
              </span>
            </div>
          </div>
        )}
        <div>
          <div style={{ color: THEME.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 5 }}>Conservation</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Badge text="AUCUNE PURGE" bg={THEME.emeraldDim} color={THEME.emerald} />
            <span style={{ color: THEME.textDim, fontSize: 11 }}>
              tout est conservé{sys.lot_size ? ` · lots de ${sys.lot_size} messages` : ""}
            </span>
          </div>
        </div>
      </div>

      {/* ── 4. FILTRES ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SujFilterRow label="Statut">
          {SUJ_STATUTS.map((s) => <SujChip key={s.id} active={statut === s.id} onClick={() => setStatut(s.id)}>
            {s.label}{data && data.par_statut && data.par_statut[s.id] != null ? ` (${data.par_statut[s.id]})` : ""}
          </SujChip>)}
        </SujFilterRow>

        <SujFilterRow label="Catégorie">
          <SujChip active={!categorie} onClick={() => setCategorie("")}>Toutes</SujChip>
          {SUJ_CATEGORIE_IDS.map((id) => {
            const m = SUJ_CATEGORIES[id];
            return <SujChip key={id} active={categorie === id} color={m.color} onClick={() => setCategorie(categorie === id ? "" : id)}
              title={m.tache ? `Génère une tâche « ${CATEGORY_LABELS[m.tache].label} »` : "Rapport seulement"}>{m.icon} {m.label}</SujChip>;
          })}
        </SujFilterRow>

        <SujFilterRow label="Tri">
          {SUJ_TRIS.map((t) => <SujChip key={t.id} active={sort === t.id} onClick={() => setSort(t.id)}>{t.label}</SujChip>)}
        </SujFilterRow>

        <SujFilterRow label="Occ. min">
          <input type="range" min={1} max={maxOcc} value={Math.min(occMin, maxOcc)} onChange={(e) => setOccMin(Number(e.target.value))}
            style={{ width: 180, accentColor: THEME.gold }} />
          <span style={{ color: THEME.text, fontSize: 12, fontWeight: 700 }}>≥ {occMin}</span>
          <span style={{ color: THEME.textDim, fontSize: 11 }}>/ {maxOcc}</span>
          {occMin > 1 && <SujChip onClick={() => setOccMin(1)}>réinitialiser</SujChip>}
        </SujFilterRow>

        <SujFilterRow label="Dernière occ.">
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
            style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: "6px 10px", fontSize: 12, outline: "none" }} />
          {since && <SujChip onClick={() => setSince("")}>effacer</SujChip>}
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            <input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setQ(qInput)}
              placeholder="Rechercher dans les titres…"
              style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: "6px 12px", fontSize: 12, outline: "none", minWidth: 180 }} />
            <button onClick={() => setQ(qInput)} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>🔍</button>
          </div>
        </SujFilterRow>
      </div>

      {/* Barre de sélection */}
      {selected.length > 0 && (
        <div style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, borderRadius: 8, padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ color: THEME.accent, fontSize: 12, fontWeight: 700 }}>{selected.length} sélectionné(s)</span>
          <button onClick={() => bulk("resolu")} style={{ background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, color: THEME.emerald, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>✓ Résoudre</button>
          <button onClick={() => bulk("ignore")} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>✕ Ignorer</button>
          <button onClick={() => bulk("ouvert")} style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, color: THEME.amber, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>↺ Réouvrir</button>
          <button onClick={() => setSelected([])} style={{ marginLeft: "auto", background: "transparent", border: "none", color: THEME.textMuted, cursor: "pointer", fontSize: 12 }}>Désélectionner</button>
        </div>
      )}

      {/* Registre */}
      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, overflow: "hidden" }}>
        {!isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: "28px 40px 1fr 110px 70px 100px 108px", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${THEME.border}`, color: THEME.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>
            <input type="checkbox" checked={!!allSelected}
              onChange={() => setSelected(allSelected ? [] : (data ? data.sujets.map((i) => i.id) : []))} />
            <div style={{ textAlign: "center" }}>Cat.</div>
            <div>Titre</div>
            <div>Sévérité</div>
            <div style={{ textAlign: "right" }}>Occ.</div>
            <div>Dernière vue</div>
            <div style={{ textAlign: "right" }}>Actions</div>
          </div>
        )}
        {loading && <div style={{ color: THEME.textMuted, fontSize: 13, padding: 20 }}>Chargement…</div>}
        {!loading && data && data.sujets.length === 0 && (
          <div style={{ color: THEME.textDim, fontSize: 13, padding: 30, textAlign: "center" }}>Aucun sujet ne correspond à ces filtres.</div>
        )}
        {!loading && data && data.sujets.map((suj) => (
          <SujRow key={suj.id} sujet={suj} token={token} isMobile={isMobile}
            selected={selected.includes(suj.id)}
            onToggleSelect={() => setSelected((prev) => prev.includes(suj.id) ? prev.filter((x) => x !== suj.id) : [...prev, suj.id])}
            onStatut={(s) => setStatutSujet(suj.id, s)}
            onTask={forcerTache}
            busy={busyId === suj.id} />
        ))}
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ color: THEME.textMuted, fontSize: 12 }}>{data ? data.total : 0} sujet(s) · page {page + 1}/{Math.max(1, totalPages)}</span>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
            style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: page === 0 ? THEME.textDim : THEME.text, borderRadius: 6, padding: "6px 14px", cursor: page === 0 ? "not-allowed" : "pointer", fontSize: 12 }}>← Préc.</button>
          <button onClick={() => setPage(page + 1)} disabled={!data || (page + 1) * LIMIT >= data.total}
            style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: !data || (page + 1) * LIMIT >= data.total ? THEME.textDim : THEME.text, borderRadius: 6, padding: "6px 14px", cursor: !data || (page + 1) * LIMIT >= data.total ? "not-allowed" : "pointer", fontSize: 12 }}>Suiv. →</button>
        </div>
      </div>

      {/* Actions bas de page */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => setShowRuns(true)}
          style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          🕒 Voir l'historique des passages
        </button>
        {canRun && (
          <button onClick={runNow} disabled={running || recapRunning}
            style={{ background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, color: THEME.purple, borderRadius: 8, padding: "7px 14px", cursor: running ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: running ? 0.6 : 1 }}>
            {running ? "⏳ Analyse en cours…" : "🔄 Analyser la dernière fenêtre"}
          </button>
        )}
        {canRun && (
          <button onClick={runRecaps} disabled={running || recapRunning}
            style={{ background: THEME.goldDim, border: `1px solid ${THEME.gold}`, color: THEME.gold, borderRadius: 8, padding: "7px 14px", cursor: recapRunning ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: recapRunning ? 0.6 : 1 }}>
            {recapRunning ? "⏳ Génération…" : "✨ Régénérer les récaps"}
          </button>
        )}
        <span style={{ color: THEME.textDim, fontSize: 11, marginLeft: "auto" }}>
          Fenêtres de {sys.fenetre_heures || 6} h · modèle {sys.model || "—"}
        </span>
      </div>

      {showRuns && <SujRunsModal token={token} canal={canal} onClose={() => setShowRuns(false)} />}
    </div>
  );
}
